import { map, share, flatMap, tap, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { Inspection, ReportPdfModel, InspectionRestoreItem, InspectionFilter, Search, DashboardStatistic } from '../models';
import { ReportService } from './report.service';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { LoadSpinnerService } from './load-spinner.service';
import { ActionPlanDataService } from './action-plan-data.service';
import { BaseInspectionService } from './baseInspection.service';
import { OfflineApiService } from './offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';
import { InspectionFactoryService } from './inspectionFactory.service';
import * as _ from 'lodash';
import { ApiMode } from '../../utils/enums';
import { SnackBarService } from './snack-bar.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class InspectionService extends BaseInspectionService implements ReportService {
  static machineSerial: string;
  static clientNumber: string;

  public loader: Observable<Inspection[]>;
  public fakeReports = [];
  private _filter: InspectionFilter;

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private loadService: LoadSpinnerService,
    private actionPlanData: ActionPlanDataService,
    private offlineApi: OfflineApiService,
    private snackBar: SnackBarService,
    private configService: ConfigService,
    private factory: InspectionFactoryService
  ) {
    super(apiService, dataService);
    if (!this.isConnection) this.loadInitialData();
  }

  private loadAllInspections() {
    const search = {
      page: 0,
      pageSize: 100000,
      filters: [
        { type: '1300', value: '100' } // ContentType == 100 (inspection)
      ]
    };
    if (this.isManager) {
      return this.apiService.post('/inspections/list', { search }).pipe(map((data) => data.Items));
    }
    // 1700=GetInspectorInspections : return owned inspections + published, scheduled, inprogress, reviewed, released or closed ones
    search.filters.push({ type: '1700', value: '100' });
    const table = AppConstants.INDEXEDDB_TABLES.inspections;
    const lastSyncDate = localStorage.getItem(`${table}${AppConstants.LAST_SYNC_DATE}_${this.applicationType}_${this.currentUserId}`);
    return this.offlineApi.postSyncList('/inspections/list', { search, lastSyncDate }, table);
  }

  loadInitialData() {
    if (this.isManager) {
      this.loader = this.loadAllInspections().pipe(
        map((inspections: Inspection[]) => {
          this.dataService.processInspections(inspections);
          this.loader = null;
          return inspections;
        }),
        share() // share allow multiple subscriptions for one call
      );
    } else {
      this.loader = this.loadAllInspections().pipe(
        flatMap((inspections: Inspection[]) => {
          if (this.actionPlanData.isLoaded()) {
            const processedInspections = this.endLoadData(inspections, _.values(this.actionPlanData.actionPlansById));
            return of(processedInspections);
          } else {
            return this.offlineApi.postSyncList('/actionplans/list', {}, 'actionPlans').pipe(
              flatMap((actionPlans) => {
                // as we had to wait for action plans, inspections table may have been updated
                // during the interval, so we have to reload updated inspections
                // it is the case when changing input language (bug on 2021 01)
                return this.offlineApi.getOfflineWithoutSync(AppConstants.INDEXEDDB_TABLES.inspections).pipe(
                  map((uptodateInspections) => {
                    const processedInspections = this.endLoadData(uptodateInspections, actionPlans);
                    return processedInspections;
                  })
                );
              })
            );
          }
        }),
        share()
      );
    }
    this.loader.subscribe(
      (data: any) => {},
      (err: any) => console.error('Error', err)
    );
  }

  private endLoadData(inspections, actionPlans) {
    actionPlans.forEach((plan) => {
      const inspection = inspections.find((i) => plan.inspection && i.id === plan.inspection.id);
      if (inspection) {
        inspection.actionPlanId = plan.id;
      }
    });
    const processedInspections = this.dataService.processInspections(inspections);
    this.loader = null;
    return processedInspections;
  }

  public getInspection(id: string, forceCheckUpdates = false, showLoading = false): Observable<Inspection> {
    const inspection = this.dataService.getInspection(id);
    let inspection$;
    if (inspection && inspection.isLoaded && !forceCheckUpdates) {
      inspection$ = new BehaviorSubject(inspection).asObservable();
    } else {
      const url = `/inspections/${id}`;
      inspection$ = this._getInspection(id, forceCheckUpdates).pipe(
        share(),
        map((insp: any) => {
          insp.isLoaded = true;
          const currentInspection = this.dataService.processInspection(insp);
          this.loadService.hideSpinner();
          if (currentInspection.parentId) {
            const parentInspection = this.dataService.getInspection(currentInspection.parentId);
            if (!parentInspection || !parentInspection.isLoaded || forceCheckUpdates) {
              this._getInspection(currentInspection.parentId, forceCheckUpdates)
                .pipe(
                  map((parinsp: any) => {
                    this.dataService.processInspection(parinsp);
                  })
                )
                .subscribe();
            }
          }
          return currentInspection;
        })
      );
    }
    return inspection$;
  }

  private _getInspection(id: string, forceCheckUpdates = false): Observable<Inspection> {
    const url = `/inspections/${id}`;
    if (this.isManager) {
      return this.apiService.get(url);
    }
    return this.offlineApi.getSyncUpdates(url, 'inspections', id, forceCheckUpdates);
  }

  public getInspections(): Observable<Inspection[]> {
    if (this.loader) {
      return this.loader;
    }
    return this.dataService.getInspections();
  }

  public addSection(id: string, sourceInspectionId: string, sourceSectionsId: string[]): Observable<Inspection> {
    return this._addSection(id, sourceInspectionId, sourceSectionsId).pipe(
      map((inspection: Inspection) => {
        inspection.isLoaded = true;
        this.dataService.processInspection(inspection);
        this.dataService.updateSectionsOrder(inspection);
        return inspection;
      })
    );
  }

  public copySection(
    id: string,
    sourceInspectionId: string,
    sourceSectionsId: string[],
    deleteSource: boolean = false,
    replaceDestination: boolean = false
  ): Observable<Inspection> {
    return this._moveSection(id, sourceInspectionId, sourceSectionsId, deleteSource, replaceDestination).pipe(
      map((response: any) => {
        if (response.updatedInspection) {
          this.dataService.processInspection(response.updatedInspection);
          this.dataService.updateSectionsOrder(response.updatedInspection);
        }

        if (response.updatedSourceInspection) {
          this.dataService.processInspection(response.updatedSourceInspection);
          this.dataService.updateSectionsOrder(response.updatedSourceInspection);
        }

        return response.updatedInspection;
      })
    );
  }

  public newInspectionFromModel(modelId: string, parentId?: string): Observable<Inspection> {
    const url = '/inspections/fromModel';
    const model = { modelId, parentId };
    if (!this.isManager) {
      return this.apiService.post(url, model).pipe(
        flatMap((inspection: Inspection) => {
          return this.factory.addInspection(inspection).pipe(
            map((res) => {
              this.dataService.processInspection(inspection);
              this.loadService.hideSpinner();
              return inspection;
            })
          );
        })
      );
    } else {
      return this.apiService.post(url, model).map((inspection: Inspection) => {
        this.dataService.processInspection(inspection);
        this.loadService.hideSpinner();
        return inspection;
      });
    }
  }

  public newInspectionFromModelJob(model: {
    modelId: string;
    name: string;
    parentId?: string;
    serialNumber?: string;
    productLineId?: string;
    machineId?: string;
    attachedTemplates?: string[];
  }): Observable<Inspection> {
    const url = '/inspections/fromModel/job';
    return this.apiService.post(url, model);
  }

  public newInspection(params: any): Observable<Inspection> {
    const url = '/inspections';
    return this.apiService.post(url, params).pipe(
      flatMap((inspection: Inspection) => {
        return this.factory.addInspection(inspection).pipe(
          map((res) => {
            this.dataService.processInspection(inspection);
            this.loadService.hideSpinner();
            return inspection;
          })
        );
      })
    );
  }

  public orderItems(id: string, orderableItems: any) {
    return this._orderItems(id, orderableItems).pipe(
      map((inspection: Inspection) => {
        const current: Inspection = this.dataService.getInspection(inspection.id);
        this.dataService.processSectionItems(current, orderableItems);
        return current;
      })
    );
  }

  public orderPlugins(id: string, orderableItems: any) {
    return this._orderPlugins(id, orderableItems).pipe(
      map((inspection: Inspection) => {
        this.dataService.updateInspectionPluginsOrder(id, orderableItems);
      })
    );
  }

  public deleteInspection(id: string, actionPlanId?: string): any {
    const url = `/inspections/${id}`;
    return this.apiService.delete(url).pipe(
      map((success: boolean) => {
        if (success) {
          this.dataService.removeInspection(id);
          if (actionPlanId) {
            this.actionPlanData.removeActionPlan(actionPlanId);
          }
        }
        return success;
      })
    );
  }

  public sendToValidate(id: string): any {
    const url = `/inspections/${id}/sendToValidate`;
    return this.apiService.post(url).pipe(
      map((success: boolean) => {
        if (success) {
          // this.dataService.removeInspection(id);
        }
        return success;
      })
    );
  }

  public updateInspectionProperties(id: string, params: any): Observable<Inspection> {
    return this._updateInspectionProperties(id, params).pipe(
      map((inspection: Inspection) => {
        this.dataService.updateInspectionProperties(inspection);
        return this.dataService.getInspection(id);
      })
    );
  }

  public updateInspectionQuotation(id: string, params: any): Observable<Inspection> {
    return this._updateInspectionQuotation(id, params).pipe(
      map((inspection: Inspection) => {
        this.dataService.updateInspectionProperties(inspection);
        return this.dataService.getInspection(id);
      })
    );
  }

  public createReport(id: string, lang: string, share: string = ''): Observable<any> {
    const url = `/reports/inspections/${id}/create/standard/v1/${lang}/${share}`;
    const fakeReport = new ReportPdfModel();
    fakeReport.name = 'Report name in progress';
    fakeReport.generationDate = new Date();
    fakeReport.status = 100;
    fakeReport.items = [];
    this.fakeReports.push(fakeReport);
    return this.apiService.get(url);
  }

  public downloadReportByType(entityId: string, type: number): any {
    const url = `api/reports/${entityId}/type/${type}/download`;
    window.open(url, '_blank');
  }

  public downloadReport(id: string): any {
    const url = `api/reports/reportItems/${id}/download`;
    window.open(url, '_blank');
  }

  public viewReport(id: string): any {
    const url = `api/reports/reportItems/${id}/view`;
    window.open(url, '_blank');
  }

  public getReports(id: string): Observable<ReportPdfModel[]> {
    const url = `/reports/${id}`;
    return this.apiService.get(url).pipe(
      map((data: ReportPdfModel[]) => {
        if (this.fakeReports.length > 0) {
          data.unshift(...this.fakeReports);
          this.fakeReports = [];
        }
        return data.map((r) => {
          const report = new ReportPdfModel();
          Object.assign(report, r);
          report.items.sort((a, b) => {
            if (a.type < b.type) {
              return -1;
            } else if (a.type > b.type) {
              return 1;
            }
            return 0;
          });
          return report;
        });
      })
    );
  }

  public updateStatus(id: string, status: number) {
    const url = '/inspections/' + id + '/status/' + status;
    return this.apiService.post(url).pipe(
      map((success: boolean) => {
        if (success) {
          this.dataService.updateStatus(id, status);
        }
        return success;
      })
    );
  }

  public getStatusList(): number[] {
    return [0, 150, 200, 250, 350];
  }

  public getInspectionCategories(applicationType: number): Observable<Inspection> {
    return this.configService.getResource('appsconfigurations/categories.json').pipe(
      map((appsCategories) => {
        return Array.isArray(appsCategories)
          ? appsCategories.find((appsCategory) => appsCategory.applicationType === applicationType)?.categories ?? []
          : [];
      })
    );
  }

  public changeInputLanguage(id: string, lang: string): Observable<Inspection> {
    const url = `/inspections/${id}/inputlanguage/${lang}`;
    return this.apiService.post(url, {});
  }

  public changeOffline(inspectionId: string, data: any): Observable<any> {
    const url = `/inspections/${inspectionId}/changeOffline`;
    return this.apiService.post(url, data).pipe(
      map((res) => {
        this.dataService.changeOffline(inspectionId, res);
        return res;
      })
    );
  }

  private _updateInspectionProperties(id: string, params: any): Observable<Inspection> {
    const url = `/inspections/${id}`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.updateInspectionProperties(id, params);
    }
    return this.apiService.patch(url, params);
  }

  private _updateInspectionQuotation(id: string, params: any): Observable<Inspection> {
    const url = `/inspections/${id}/quotation`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.updateInspectionProperties(id, params);
    }
    return this.apiService.patch(url, params);
  }

  private _orderPlugins(id: string, orderableItems: any) {
    const url = `/inspections/${id}/orderplugins`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.orderInspectionPlugins(id, orderableItems);
    }
    return this.apiService.post(url, { OrderableItems: orderableItems });
  }

  public _orderItems(id: string, orderableItems: any) {
    const url = `/inspections/${id}/ordersections`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.orderSections(id, orderableItems);
    }
    return this.apiService.post(url, { OrderableItems: orderableItems });
  }

  private _addSection(id: string, sourceInspectionId: string, sourceSectionsId: string[]): Observable<Inspection> {
    const url = `/inspections/${id}/sections`;
    const data = { id, sourceInspectionId, sourceSectionsId };
    if (this.isInspectionInOffline(id)) {
      return this.factory.importSections(data);
    }
    return this.apiService
      .post(url, data)
      .pipe(switchMap((inspection) => this.factory.addInspection(inspection).pipe(map(() => inspection))));
  }

  private _moveSection(
    id: string,
    sourceInspectionId: string,
    sourceSectionsId: string[],
    deleteSource: boolean = false,
    replaceDestination: boolean = false
  ): Observable<{ updatedInspection: Inspection; updatedSourceInspection?: Inspection }> {
    const data = { id, sourceInspectionId, sourceSectionsId, markAsCopy: !(deleteSource || replaceDestination) };
    if (this.isInspectionInOffline(id)) {
      const sourceUrl = `/inspections/${sourceSectionsId}`;
      const source$ = this.isInspectionInOffline(sourceSectionsId)
        ? this.offlineApi.getWithMode(sourceUrl, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.inspections, sourceInspectionId)
        : this._getInspection(data.sourceInspectionId);
      return this.factory
        .copySections(id, source$, sourceSectionsId, !(deleteSource || replaceDestination))
        .switchMap((updatedInspection) => {
          if (deleteSource) {
            if (this.isInspectionInOffline(sourceInspectionId)) {
              return this.factory
                .deleteSections(sourceInspectionId, sourceSectionsId)
                .map((updatedSourceInspection) => ({ updatedInspection, updatedSourceInspection }));
            } else {
              const sourceDeleteUrl = `/inspections/${sourceInspectionId}/sections/delete`;
              return this.apiService
                .post(sourceDeleteUrl, sourceSectionsId)
                .map((updatedSourceInspection) => ({ updatedInspection, updatedSourceInspection }));
            }
          }
          return of({ updatedInspection });
        });
    } else if (this.isInspectionInOffline(sourceInspectionId)) {
      this.snackBar.warn('snack-bar.something-went-wrong');
      return of(null);
    }
    if (deleteSource) {
      const url = `/inspections/${id}/sections/move`;
      return this.apiService
        .post(url, data)
        .pipe(
          switchMap(({ updatedInspection, updatedSourceInspection }) =>
            this.factory.addInspection(updatedInspection).pipe(map(() => ({ updatedInspection, updatedSourceInspection })))
          )
        );
    } else {
      const url = `/inspections/${id}/sections`;
      return this.apiService
        .post(url, data)
        .pipe(switchMap((updatedInspection) => this.factory.addInspection(updatedInspection).pipe(map(() => ({ updatedInspection })))));
    }
  }

  public synchronize(inspectionId: string) {
    const url = `/synchronization/upload/inspections/${inspectionId}`;
    const inspection = this.dataService.getInspection(inspectionId);
    return this.apiService.post(url, { inspection: { name: 'test synchronization' }, points: [] }).pipe(
      map((res) => {
        return res;
      })
    );
  }

  public getInspectionRestoreItems(inspectionId: string) {
    const url = `/inspections/${inspectionId}/restoreitems`;
    return this.apiService.get(url).pipe(
      map((items: InspectionRestoreItem[]) => {
        return this.dataService.processRestoreItems(inspectionId, items);
      })
    );
  }

  public restoreInspectionItems(inspectionId: string, items: InspectionRestoreItem[]) {
    const url = `/inspections/${inspectionId}/restoreitems`;
    return this.apiService.post(url, items).pipe(
      map((res) => {
        if (res.sections && res.sections.length > 0) {
          res.sections.forEach((s) => this.dataService.updateSection(inspectionId, s));
        }
        if (res.points && res.points.length > 0) {
          res.points.forEach((point) => this.dataService.updatePoint(inspectionId, point));
        }
        if (res.plugins && res.plugins.length > 0) {
          this.dataService.addPlugin(inspectionId, res.plugins);
        }
        return res;
      })
    );
  }

  public getInspectionEntity(id: string): Observable<any> {
    const url = `/inspections/${id}/entity`;
    return this.apiService.get(url);
  }

  public updateInspectionEntity(inspection: any): Observable<any> {
    const url = `/inspections/${inspection.Id}/entity`;
    return this.apiService.put(url, inspection);
  }

  public updateFromMachineModule(id: string): Observable<any> {
    return this.apiService.get(`/inspections/${id}/updatefrommachinemodule`);
  }

  public updateMachineModule(id: any): Observable<any> {
    return this.apiService.get(`/inspections/${id}/machinemodule`);
  }

  public exportInspections(search: Search): any {
    const url = `/inspections/filteredlist/export`;
    this.apiService.downloadAndOpen(url, `inspections_${new Date().toLocaleString()}.xlsx`, search);
  }

  getFilteredInspections(search: Search): Observable<Inspection[]> {
    return this.apiService.post('/inspections/filteredlist', search).pipe(
      map((inspections: Inspection[]) => {
        if (this.isManager) return inspections;
        return inspections.filter((i) => i.id !== this.offlineInspectionId);
      })
    );
  }

  getCreatingInspections(model: any): Observable<Inspection[]> {
    return this.apiService.post(`/inspections/creatinglist`, model, { ignoreLoader: 'true' });
  }

  getOfflineInspection(): Observable<Inspection> {
    if (this.offlineInspectionId) {
      return this.getInspection(this.offlineInspectionId);
    } else {
      return of(null);
    }
  }

  getinspectionStatisticByPerson(personId = ''): Observable<DashboardStatistic> {
    if (!this.isConnection) {
      return of(JSON.parse(localStorage.getItem(AppConstants.DASHBOARD_STATISTIC)));
    }
    return this.apiService
      .get(`/inspections/statistics/dashboard/${personId}`)
      .pipe(tap((res) => localStorage.setItem(AppConstants.DASHBOARD_STATISTIC, JSON.stringify(res))));
  }

  getSectionItems(id: string, headers?: { [header: string]: string | string[] }): Observable<Inspection> {
    return this.apiService.get(`/inspections/${id}/sectionitems`, headers);
  }

  getProperties(id: string, headers?: { [header: string]: string | string[] }): Observable<Inspection> {
    return this.apiService.get(`/inspections/${id}/properties`, headers);
  }

  get filter() {
    return this._filter ? { ...this._filter } : null;
  }

  set filter(value: InspectionFilter) {
    this._filter = { ...value };
  }

  private getActionPlans() {
    if (this.actionPlanData.isLoaded()) {
      return of(_.values(this.actionPlanData.actionPlansById));
    } else {
      return this.offlineApi.postSyncList('/actionplans/list', {}, 'actionPlans').pipe(
        map((actionPlans) => {
          this.actionPlanData.processActionPlans(actionPlans);
          return actionPlans;
        })
      );
    }
  }
}
